import { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BUSINESS_PLAN_TYPES } from 'constants/businessPlan';
import { IOnePageStyles, ONE_PAGE_DISPLAY_MODE, ONE_PAGE_STYLE_OPTIONS } from 'layouts/OnePagePreviewLayout';
import { useBusinessToolSessionStore, useBusinessToolStore } from 'store/useBusinessToolStore';
import {
  getBusinessPlanMetadata,
  getBusinessPlanType,
  getOnePageDisplayMode,
} from 'store/useBusinessToolStore/selectors';

import { EXAMPLE_DOC } from './constants/exampleDoc';
import { EXAMPLE_DOC_DE } from './constants/exampleDoc_de';
import { EXAMPLE_DOC_ES } from './constants/exampleDoc_es';
import { EXAMPLE_DOC_UK } from './constants/exampleDoc_uk';
import { DataContextProvider } from './context';
import { OnePageFirstStyleView } from './OnePageFirstStyle';
import { OnePageSecondStyleView } from './OnePageSecondStyle';
import { OnePageThirdStyleView } from './OnePageThirdStyle';
import { getOnePagePreviewData } from './storeSelectors';
import { IOnePageViewProps } from './types';

const getStyleView = (onePageTemplateStyle: IOnePageStyles) => {
  switch (onePageTemplateStyle) {
    case ONE_PAGE_STYLE_OPTIONS.SECOND_STYLE:
      return <OnePageSecondStyleView />;
    case ONE_PAGE_STYLE_OPTIONS.THIRD_STYLE:
      return <OnePageThirdStyleView />;
    default:
      return <OnePageFirstStyleView />;
  }
};

export const OnePagePreviewContainer: FC = memo(() => {
  const { i18n } = useTranslation();
  const { onePageTemplateStyle } = useBusinessToolStore(getBusinessPlanMetadata);
  const storeData = useBusinessToolStore(getOnePagePreviewData);
  const businessPlanType = useBusinessToolStore(getBusinessPlanType);
  const onePageDisplayMode = useBusinessToolSessionStore(getOnePageDisplayMode);

  const getChosenOnePageExample = (lang: string) => {
    switch (lang) {
      case 'uk':
        return EXAMPLE_DOC_UK;
      case 'de':
        return EXAMPLE_DOC_DE;
      case 'es':
        return EXAMPLE_DOC_ES;

      default:
        return EXAMPLE_DOC;
    }
  };

  const isOnePageExampleShown = onePageDisplayMode === ONE_PAGE_DISPLAY_MODE.EXAMPLE;

  const isExtendedPreview = businessPlanType === BUSINESS_PLAN_TYPES.ONE_PAGE;
  const data = isOnePageExampleShown ? getChosenOnePageExample(i18n.language) : storeData;

  const contextValue = useMemo<IOnePageViewProps>(() => ({ isExtendedPreview, data }), [isExtendedPreview, data]);

  return <DataContextProvider value={contextValue}>{getStyleView(onePageTemplateStyle)}</DataContextProvider>;
});
